import { memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BaseBarChart } from '@/components/Charts/BaseBarChart';
import { ProjectMetrics } from '@/utils/calculations';
import { formatCurrency } from '@/utils/format';
import { BarChart3 } from 'lucide-react';

interface RevenueVsCostChartProps {
  metrics: ProjectMetrics;
}

export const RevenueVsCostChart = memo(function RevenueVsCostChart({ metrics }: RevenueVsCostChartProps) {
  const { totalRevenue, totalCosts, profit } = metrics;

  if (!totalRevenue && !totalCosts) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Value vs Costs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex flex-col items-center justify-center text-muted-foreground">
            <BarChart3 className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-center">
              Enter contract values and costs to see the comparison.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isLoss = profit < 0;

  const chartData = [{
    name: '',
    'Total Value': totalRevenue,
    'Total Costs': totalCosts,
  }];

  const chartConfig = {
    margin: { top: 20, right: 30, bottom: 50, left: 70 },
    xAxis: {
      angle: 0,
      textAnchor: 'middle' as const,
      interval: 0,
      fontSize: 12,
    },
    yAxis: {
      width: 60,
      tickFormatter: (value: number) => `£${(value / 1000).toFixed(0)}k`,
    },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          Value vs Costs
          <span className={`text-sm font-normal ${isLoss ? 'text-destructive' : 'text-green-600'}`}>
            {isLoss ? 'Loss' : 'Profit'}: {formatCurrency(Math.abs(profit))}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <BaseBarChart
            data={chartData}
            dataKeys={['Total Value', 'Total Costs']}
            colors={[
              'hsl(var(--chart-1))',
              isLoss ? 'hsl(var(--destructive))' : 'hsl(var(--chart-2))'
            ]}
            tooltipFormatter={(value: number) => formatCurrency(value)}
            margin={chartConfig.margin}
            xAxis={chartConfig.xAxis}
            yAxis={chartConfig.yAxis}
          />
        </div>
      </CardContent>
    </Card>
  );
});
